import {Actions} from "vuex-smart-module";
import RootState from "@/store/modules/root-state";
import RootGetters from "@/store/modules/root-getters";
import RootMutations from "@/store/modules/root-mutations";
import ICommand from "@/types/interfaces/i-command";
import CommandFailedError from "@/types/errors/command-failed-error";
import eventBus from "@/modules/event-bus";
import type {MessageOptions} from "element-plus";

export default class RootActions extends Actions<RootState, RootGetters, RootMutations, RootActions> {
    private $events = eventBus;

    async message(options: MessageOptions): Promise<void> {
        this.$events.emit("message", options);
    }

    async notifyNetworkError(): Promise<void> {
        await this.actions.message({
            message: "Unable to connect to the server. Check your internet connection and try again.",
            type: "error",
            duration: 0,
            showClose: true
        });
    }

    async runCommands(): Promise<void> {
        let performedCommands = 0;

        for (const command of this.state.commands) {
            try {
                await command.execute();
            } catch (e: unknown) {
                const error = e instanceof Error ? e : new Error(String(e));
                await this.actions.onError(error);
            }

            performedCommands++;
        }

        this.mutations.sliceCommands(performedCommands);
    }

    async onCommand(command: ICommand): Promise<void> {
        const offlineModeIsPermitted = process.env.VUE_APP_PERMIT_OFFLINE_MODE === "true";

        if (this.state.isOffline) {
            if (offlineModeIsPermitted) {
                this.mutations.addCommand(command);
                return;
            }

            await this.actions.onError(new CommandFailedError(
                "No internet connection.",
                "Unable to connect to server. Please ensure you are connected to the internet."));
        }

        try {
            await command.execute();
        } catch (e: unknown) {
            const error = e instanceof Error ? e : new Error(String(e));
            await this.actions.onError(error);
        }
    }

    async onError(e: Error): Promise<void> {
        if (e instanceof CommandFailedError) {
            await this.actions.handleCommandFailedError(e);
        }

        throw e;
    }

    async handleCommandFailedError(e: CommandFailedError): Promise<void> {
        const alert = e.getAlert() ?? "Action failed. Server did not return a response."

        await this.actions.message({
            message: alert,
            type: "error",
            duration: 0,
            showClose: true
        });
    }
}
